import { MouseEventHandler, ReactNode } from 'react'

import {
  Avatar,
  Button,
  ButtonProps,
  Text,
} from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, BoxProps, Flex, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { HealthtoolReviewer } from 'interfaces/types'
import { renderDate } from 'utils/renderDate'

import { Disclaimer, DisclaimerProps } from './disclaimer'
import useStyles from './index.styles'

type RiskScreenerLayoutProps = {
  title: string
  icon?: string
  description?: string
  url?: string
  ctaTitle?: string
  ctaBtnProps?: Partial<ButtonProps>
  ctaFn?: MouseEventHandler<HTMLButtonElement>
  reviewedBy?: HealthtoolReviewer
  bodyRatio?: 'none' | number
  children?: ReactNode
  disclaimer?: {
    heading: DisclaimerProps['heading']
    description: DisclaimerProps['description']
  }
  applyH1?: boolean
} & BoxProps

export const RiskScreenerLayout = ({
  icon,
  title,
  description,
  disclaimer,
  reviewedBy,
  url,
  ctaTitle,
  ctaFn,
  ctaBtnProps,
  bodyRatio = 648 / 486,
  children,
  styles,
  classNames,
  applyH1,
  ...rest
}: RiskScreenerLayoutProps) => {
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const { classes } = useStyles(undefined, {
    name: 'RiskScreenerLayout',
    styles,
    classNames,
  })

  const btnEle =
    url && ctaTitle ? (
      <Box className={classes.ctaBtn}>
        <Button fullWidth variant="primary" onClick={ctaFn} {...ctaBtnProps}>
          {ctaTitle}
        </Button>
      </Box>
    ) : null

  const disclaimerProps = {
    heading: disclaimer?.heading || '',
    description: disclaimer?.description || '',
    classNames: {
      root: classes.disclaimerRoot,
      targetBtn: classes.disclaimerTargetBtn,
      dropdown: classes.disclaimerDropdown,
      icon: classes.disclaimerIcon,
    },
  }

  return (
    <Box
      className={classes.root}
      styles={styles}
      classNames={classNames}
      {...rest}
    >
      <Container className={classes.container}>
        <Box className={classes.infoCol}>
          <Box className={classes.infoTop}>
            <Box className={classes.info}>
              {icon && (
                <Box className={classes.iconWrapper}>
                  <img
                    src={icon}
                    alt={title}
                    loading="lazy"
                    className={classes.icon}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                </Box>
              )}
              {title && (
                <Text
                  size="h1"
                  as={applyH1 ? 'h1' : 'h2'}
                  className={classes.heading}
                >
                  {title}
                </Text>
              )}
            </Box>
            {description && <Text size="p3">{description}</Text>}
            {!isMobile && btnEle}
            {reviewedBy?.reviewer && (
              <Flex
                gap={12}
                sx={(theme) => ({
                  alignItems: 'center',
                  [theme.fn.smallerThan('sm')]: {
                    position: 'relative',
                    zIndex: 1,
                    // paddingBottom: rem(20),
                    alignItems: 'flex-start',
                  },
                })}
              >
                <Box>
                  <Avatar
                    size={32}
                    src={reviewedBy.avatar}
                    withIndicator
                    indicatorConfig={{
                      size: 18,
                      label: (
                        <img
                          loading="lazy"
                          width="16"
                          src={`${assetImagePath}/reviewer-medical.svg`}
                          alt=""
                        />
                      ),
                    }}
                    styles={{
                      root: {
                        '.mantine-Indicator-indicator': {
                          bottom: '7px',
                          right: '5px',
                        },
                        '.mantine-Avatar-placeholder': {
                          border: 0,
                        },
                      },
                    }}
                  />
                </Box>
                <Text
                  size={isMobile ? 'p3' : 'p4'}
                  color="gray.6"
                  as="div"
                  sx={(theme) => ({
                    lineHeight: `${rem(24)}!important`,
                    [theme.fn.largerThan('sm')]: {
                      div: {
                        display: 'inline',
                      },
                    },
                  })}
                >
                  <div>
                    {f(
                      { id: 'articlePage.reviewed.factCheck' },
                      {
                        name: (
                          <div>
                            <a
                              href={reviewedBy.link || '#'}
                              style={{ color: '#262626' }}
                            >
                              {reviewedBy.reviewer}
                            </a>
                            {!isMobile && reviewedBy.date && ' • '}
                          </div>
                        ),
                      },
                    )}{' '}
                  </div>
                  {reviewedBy.date && (
                    <div>{renderDate(reviewedBy.date, locale)}</div>
                  )}
                </Text>
              </Flex>
            )}
          </Box>
          {!isMobile && disclaimer?.heading && disclaimer?.description && (
            <Disclaimer maxHeight={90} {...disclaimerProps} />
          )}
        </Box>
        <Box className={classes.imgCol}>
          {bodyRatio === 'none' ? (
            <Box className={classes.boxFrame}>
              <Box className={classes.boxFrameInner}>{children}</Box>
            </Box>
          ) : (
            <AspectRatio ratio={bodyRatio} className={classes.boxFrame}>
              <Box className={classes.boxFrameInner}>{children}</Box>
            </AspectRatio>
          )}
        </Box>
        {isMobile && disclaimer?.heading && disclaimer?.description && (
          <>
            {btnEle}
            <Disclaimer {...disclaimerProps} />
          </>
        )}
      </Container>
    </Box>
  )
}
